// ./src/components/features/ParkingSession/ActiveParkingDetailDrawer.tsx
//
// Detail drawer for an active parking session.
//
// Context integration:
// - Reads updateParking/endParking/isUpdating/isEnding from ActiveParkingContext
// - Closes via setParkingSession(null) from LayoutUI when session is ended
// - All operation props (onUpdate, onEnd, updating, ending) are optional overrides
// - When no props are passed, falls back to context methods
// - When props are passed, they win (so DashboardLayout can still override if needed)
//
// Photo display:
// - Shows thumbnail in detail mode if session has an uploaded photo
// - Tap thumbnail for full-screen preview via ImageViewer

import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  Stack,
  Group,
  Text,
  NumberInput,
  Textarea,
  Box,
  Divider,
  SegmentedControl,
  Progress,
  Badge,
  Image,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useTranslation } from "react-i18next";

import {
  ParkingSession,
  ParkingSessionInput,
  PARKING_SESSION_STATUS,
  getSessionRemainingMinutes,
  getSessionElapsedMinutes,
  isSessionExpired,
  ParkingSessionEndProps,
} from "@/utils/api";
import { localStringToUtc, getCurrentLocalDateTimeString } from "@/utils";
import { useActiveParkingContext, useAppEnvironment, useLayoutUI } from "@/contexts";

import {
  AppDrawer,
  AppDrawerAction,
  AppButton,
  AppIcon,
  CurrencySelector,
  LocationPreview,
  ImageViewer,
} from "@/components/generic";
import { TravelSelector } from "@/components/features/Travels";

// ============================================
// Types
// ============================================

export interface ActiveParkingDetailDrawerProps {
  opened: boolean;
  onClose: () => void;
  session: ParkingSession | null;

  /**
   * Optional override for the update operation.
   * If not provided, uses ActiveParkingContext.updateParking.
   */
  onUpdate?: (id: string, params: ParkingSessionInput) => Promise<void>;

  /**
   * Optional override for the end operation.
   * If not provided, uses ActiveParkingContext.endParking and
   * closes the drawer via LayoutUI.setParkingSession(null) on success.
   */
  onEnd?: (id: string, params: ParkingSessionEndProps) => Promise<void>;

  /**
   * Optional override for the updating state.
   * If not provided, uses ActiveParkingContext.isUpdating.
   */
  updating?: boolean;

  /**
   * Optional override for the ending state.
   * If not provided, uses ActiveParkingContext.isEnding.
   */
  ending?: boolean;
}

type DrawerMode = "detail" | "end";

// ============================================
// Constants
// ============================================

/** Thumbnail height for photo preview in detail mode */
const PHOTO_THUMB_HEIGHT = 140;

// ============================================
// Add Time Presets
// ============================================

const ADD_TIME_PRESETS = [
  { value: "15", labelKey: "parking:drawers.detail.addTime.15min" },
  { value: "30", labelKey: "parking:drawers.detail.addTime.30min" },
  { value: "60", labelKey: "parking:drawers.detail.addTime.1h" },
  { value: "custom", labelKey: "parking:drawers.detail.addTime.custom" },
];

// ============================================
// Helpers
// ============================================

const formatDuration = (minutes: number | null): string => {
  if (minutes == null) return "--";
  const absMinutes = Math.abs(minutes);
  const hrs = Math.floor(absMinutes / 60);
  const mins = absMinutes % 60;
  if (hrs === 0) return `${mins}m`;
  if (mins === 0) return `${hrs}h`;
  return `${hrs}h ${mins}m`;
};

const getProgressPercent = (session: ParkingSession): number => {
  if (!session.startTime || !session.durationMinutes) return 0;
  const elapsed = getSessionElapsedMinutes(session);
  if (elapsed == null) return 0;
  return Math.min(100, Math.max(0, (elapsed / session.durationMinutes) * 100));
};

// ============================================
// Component
// ============================================

const ActiveParkingDetailDrawerComponent: React.FC<
  ActiveParkingDetailDrawerProps
> = ({
  opened,
  onClose,
  session,
  onUpdate: onUpdateProp,
  onEnd: onEndProp,
  updating: updatingProp,
  ending: endingProp,
}) => {
    const { t } = useTranslation(["parking", "parkingSessions", "common"]);
    const { isMobile } = useAppEnvironment();

    // ── Context resolution ──
    const {
      updateParking,
      endParking,
      isUpdating: contextUpdating,
      isEnding: contextEnding,
    } = useActiveParkingContext();
    const { setParkingSession } = useLayoutUI();

    const updating = updatingProp ?? contextUpdating;
    const ending = endingProp ?? contextEnding;

    // Build effective update handler
    const handleUpdateFn = useCallback(
      async (id: string, params: ParkingSessionInput) => {
        if (onUpdateProp) {
          await onUpdateProp(id, params);
        } else {
          const result = await updateParking(id, params);
          // If update succeeded and session is still active, update LayoutUI
          // so the drawer shows fresh data
          if (result) {
            setParkingSession(result);
          }
        }
      },
      [onUpdateProp, updateParking, setParkingSession]
    );

    // Build effective end handler
    const handleEndFn = useCallback(
      async (id: string, params: ParkingSessionEndProps) => {
        if (onEndProp) {
          await onEndProp(id, params);
        } else {
          const result = await endParking(id, params);
          // On success, close the drawer via LayoutUI
          if (result?.id) {
            setParkingSession(null);
          }
        }
      },
      [onEndProp, endParking, setParkingSession]
    );

    // ============================================
    // State
    // ============================================

    const [mode, setMode] = useState<DrawerMode>("detail");

    // Add time state
    const [showAddTime, setShowAddTime] = useState(false);
    const [addTimePreset, setAddTimePreset] = useState<string>("30");
    const [customAddMinutes, setCustomAddMinutes] = useState<number | null>(null);

    // Travel state (shared between detail and end modes)
    const [travelId, setTravelId] = useState<string | null>(null);
    const [travelDirty, setTravelDirty] = useState(false);

    // End session form state
    const [endStartTime, setEndStartTime] = useState<string>("");
    const [endTime, setEndTime] = useState<string>("");
    const [finalPrice, setFinalPrice] = useState<number | null>(null);
    const [endCurrency, setEndCurrency] = useState<string | null>(null);
    const [endNotes, setEndNotes] = useState<string>("");

    // Photo preview state
    const [isPhotoPreviewOpen, setIsPhotoPreviewOpen] = useState(false);

    // Timer tick state
    const [, setTick] = useState(0);
    const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // ============================================
    // Timer: tick every 30s
    // ============================================

    useEffect(() => {
      if (opened && session?.status === PARKING_SESSION_STATUS.ACTIVE) {
        tickRef.current = setInterval(() => setTick((p) => p + 1), 30_000);
        return () => { if (tickRef.current) clearInterval(tickRef.current); };
      }
      return () => { if (tickRef.current) clearInterval(tickRef.current); };
    }, [opened, session?.status]);

    // ============================================
    // Reset state when drawer opens / session changes
    // ============================================

    useEffect(() => {
      if (opened && session) {
        setMode("detail");
        setShowAddTime(false);
        setAddTimePreset("30");
        setCustomAddMinutes(null);
        setTravelId(session.travelId ?? null);
        setTravelDirty(false);
        setEndStartTime(session.startTime || "");
        setEndTime(getCurrentLocalDateTimeString());
        setFinalPrice(session.initialPrice ?? null);
        setEndCurrency(session.currency || null);
        setEndNotes(session.notes || "");
        setIsPhotoPreviewOpen(false);
      }
    }, [opened, session?.id]);

    // ============================================
    // Derived values
    // ============================================

    const hasDuration = !!session?.durationMinutes;
    const expired = session ? isSessionExpired(session) : false;
    const remainingMinutes = session ? getSessionRemainingMinutes(session) : null;
    const elapsedMinutes = session ? getSessionElapsedMinutes(session) : null;
    const progressPercent = session ? getProgressPercent(session) : 0;
    const hasCoordinates = session?.latitude != null && session?.longitude != null;
    const isLoading = updating || ending;

    // Photo: resolve URLs from nested uploadedFile relation
    const photoUrl = useMemo(() => {
      if (!session?.uploadedFileId) return null;
      const uf = (session as any).uploadedFile;
      return uf?.fileUrl || uf?.thumbnailUrl || null;
    }, [session?.uploadedFileId, (session as any)?.uploadedFile]);

    const photoThumbUrl = useMemo(() => {
      if (!session?.uploadedFileId) return null;
      const uf = (session as any).uploadedFile;
      return uf?.thumbnailUrl || uf?.fileUrl || null;
    }, [session?.uploadedFileId, (session as any)?.uploadedFile]);

    const photoFileName = useMemo(() => {
      if (!session?.uploadedFileId) return null;
      const uf = (session as any).uploadedFile;
      return uf?.name || uf?.originalFilename || null;
    }, [session?.uploadedFileId, (session as any)?.uploadedFile]);

    const hasPhoto = Boolean(photoUrl);

    // Theme-aware status colors
    const statusColor = expired ? "error" : "brand";
    const statusShade = expired ? 5 : 6;

    const addTimeMinutes = useMemo(() => {
      if (addTimePreset === "custom") return customAddMinutes;
      const parsed = parseInt(addTimePreset, 10);
      return isNaN(parsed) ? null : parsed;
    }, [addTimePreset, customAddMinutes]);

    const endTotalDuration = useMemo(() => {
      if (!endStartTime || !endTime) return null;
      const start = new Date(endStartTime);
      const end = new Date(endTime);
      const diffMs = end.getTime() - start.getTime();
      return diffMs > 0 ? Math.round(diffMs / 60000) : 0;
    }, [endStartTime, endTime]);

    // ============================================
    // Handlers: Detail mode
    // ============================================

    const handleToggleAddTime = useCallback(() => {
      setShowAddTime((prev) => !prev);
    }, []);

    const handleAddTimePresetChange = useCallback((value: string) => {
      setAddTimePreset(value);
      if (value !== "custom") setCustomAddMinutes(null);
    }, []);

    const handleCustomAddMinutesChange = useCallback(
      (value: string | number) => {
        setCustomAddMinutes(typeof value === "number" ? value : null);
      },
      []
    );

    const handleConfirmAddTime = useCallback(async () => {
      if (!session?.id || !addTimeMinutes) return;
      const newDuration = (session.durationMinutes || 0) + addTimeMinutes;
      await handleUpdateFn(session.id, { durationMinutes: newDuration });
      setShowAddTime(false);
      setTick((prev) => prev + 1);
    }, [session?.id, session?.durationMinutes, addTimeMinutes, handleUpdateFn]);

    const handleTravelChange = useCallback(
      (_name: string, value: string | null) => {
        setTravelId(value);
        setTravelDirty(true);
      },
      []
    );

    const handleSaveTravelChange = useCallback(async () => {
      if (!session?.id || !travelDirty) return;
      await handleUpdateFn(session.id, { travelId: travelId || null });
      setTravelDirty(false);
    }, [session?.id, travelId, travelDirty, handleUpdateFn]);

    const handleSwitchToEndMode = useCallback(() => {
      setEndTime(getCurrentLocalDateTimeString());
      setMode("end");
    }, []);

    const handleBackToDetail = useCallback(() => {
      setMode("detail");
    }, []);

    // Photo preview handlers
    const handleOpenPhotoPreview = useCallback(() => {
      if (photoUrl) setIsPhotoPreviewOpen(true);
    }, [photoUrl]);

    const handleClosePhotoPreview = useCallback(() => {
      setIsPhotoPreviewOpen(false);
    }, []);

    // ============================================
    // Handlers: End mode
    // ============================================

    const handleEndStartTimeChange = useCallback((value: string | null) => {
      setEndStartTime(value || "");
    }, []);

    const handleEndTimeChange = useCallback((value: string | null) => {
      setEndTime(value || getCurrentLocalDateTimeString());
    }, []);

    const handleFinalPriceChange = useCallback((value: string | number) => {
      setFinalPrice(value ? Number(value) : null);
    }, []);

    const handleEndCurrencyChange = useCallback(
      (_name: string | undefined, value: string | null) => {
        setEndCurrency(value);
      },
      []
    );

    const handleEndNotesChange = useCallback(
      (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setEndNotes(event.target.value);
      },
      []
    );

    const handleConfirmEnd = useCallback(async () => {
      if (!session?.id) return;
      await handleEndFn(session.id, {
        startTime: endStartTime ? localStringToUtc(endStartTime) : undefined,
        endTime: endTime ? localStringToUtc(endTime) : undefined,
        finalPrice: finalPrice != null && finalPrice > 0 ? finalPrice : undefined,
        currency: endCurrency || undefined,
        travelId: travelId || undefined,
        notes: endNotes || undefined,
      });
    }, [session?.id, endStartTime, endTime, finalPrice, endCurrency, travelId, endNotes, handleEndFn]);

    // ============================================
    // Add time segment data
    // ============================================

    const addTimeSegmentData = useMemo(
      () =>
        ADD_TIME_PRESETS.map((p) => ({
          value: p.value,
          label: t(p.labelKey, { defaultValue: p.value }),
        })),
      [t]
    );

    // ============================================
    // AppDrawer action configuration per mode
    // (must be above the early-return guard to keep hook order stable)
    // ============================================
    //
    // Detail mode:  [End Parking (danger, left)] ............. [Close]
    // End mode:     [Back (left)]       ............. [Confirm & End]

    const drawerActionType = "custom" as const;

    const drawerLeftActions: AppDrawerAction[] = useMemo(() => {
      if (mode === "detail") {
        return [
          {
            label: t("parking:drawers.detail.actions.endParking", {
              defaultValue: "End Parking",
            }),
            onClick: handleSwitchToEndMode,
            variant: "danger" as const,
            iconName: "ParkingOff",
            disabled: isLoading,
          },
        ];
      }

      // End mode: back button
      return [
        {
          label: t("parking:drawers.end.actions.back", {
            defaultValue: "Back",
          }),
          onClick: handleBackToDetail,
          variant: "secondary" as const,
          iconName: "ArrowLeft",
          disabled: isLoading,
        },
      ];
    }, [mode, isLoading, t, handleSwitchToEndMode, handleBackToDetail]);

    const drawerCustomActions = useMemo(() => {
      if (mode === "detail") {
        return (
          <AppButton
            variant="secondary"
            size="lg"
            onClick={onClose}
            disabled={isLoading}
          >
            {t("common:buttons.close", { defaultValue: "Close" })}
          </AppButton>
        );
      }

      // End mode: confirm button
      return (
        <AppButton
          variant="primary"
          color="red"
          size="lg"
          iconName="ParkingOff"
          onClick={handleConfirmEnd}
          loading={ending}
          disabled={isLoading}
        >
          {t("parking:drawers.end.actions.confirm", {
            defaultValue: "Confirm & End",
          })}
        </AppButton>
      );
    }, [mode, isLoading, ending, t, onClose, handleConfirmEnd]);

    // ============================================
    // Drawer title (safe even when session is null)
    // ============================================

    const drawerTitle =
      mode === "end"
        ? t("parking:drawers.detail.endTitle", { defaultValue: "End Parking" })
        : t("parking:drawers.detail.title", { defaultValue: "Active Parking" });

    // ============================================
    // Render: Travel Section (shared between modes)
    // ============================================

    const renderTravelSection = (editable: boolean) => (
      <Stack gap="sm">
        <TravelSelector
          name="travelId"
          value={travelId}
          onChange={handleTravelChange}
          carId={session?.carId ?? null}
          preselectActiveTravel={false}
          label={t("common:selectors.travel.label")}
          placeholder={t("common:selectors.travel.placeholder")}
          size="md"
        />
        <Text size="xs" c="dimmed">
          {t("common:selectors.travel.hint")}
        </Text>
        {/* Save button in detail mode when travel was changed */}
        {editable && travelDirty && (
          <AppButton
            variant="primary"
            size="xs"
            iconName="DeviceFloppy"
            onClick={handleSaveTravelChange}
            loading={updating}
            disabled={isLoading}
          >
            {t("parking:drawers.detail.actions.saveTravel", {
              defaultValue: "Save Travel",
            })}
          </AppButton>
        )}
      </Stack>
    );

    // ============================================
    // Render: Photo Section (detail mode, read-only)
    // ============================================

    const renderPhotoSection = () => {
      if (!hasPhoto) return null;

      return (
        <>
          <Divider
            label={t("parkingSessions:form.sections.photo", {
              defaultValue: "Photo",
            })}
            labelPosition="center"
          />

          <Box
            onClick={handleOpenPhotoPreview}
            style={{
              position: "relative",
              borderRadius: "var(--mantine-radius-md)",
              overflow: "hidden",
              border: "1px solid var(--mantine-color-neutral-2)",
              cursor: "pointer",
              height: PHOTO_THUMB_HEIGHT,
            }}
          >
            <Image
              src={photoThumbUrl || photoUrl}
              alt={
                photoFileName ||
                t("parkingSessions:form.photo.alt", {
                  defaultValue: "Parking photo",
                })
              }
              fit="cover"
              h={PHOTO_THUMB_HEIGHT}
              w="100%"
              style={{ display: "block" }}
            />

            {/* Tap hint */}
            <Box
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                padding: "6px 12px",
                background:
                  "linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 100%)",
              }}
            >
              <Text size="xs" c="white" ta="center">
                {t("parkingSessions:form.photo.tapToPreview", {
                  defaultValue: "Tap to preview",
                })}
              </Text>
            </Box>
          </Box>
        </>
      );
    };

    // ============================================
    // Render: Detail Mode content
    // ============================================

    const renderDetailMode = () => (
      <Stack gap="md">
        {/* ── Timer Section ── */}
        <Box
          p="md"
          style={{
            backgroundColor: `var(--mantine-color-${statusColor}-0)`,
            borderRadius: "var(--mantine-radius-md)",
            border: `1px solid var(--mantine-color-${statusColor}-3)`,
            textAlign: "center",
          }}
        >
          <Badge color={statusColor} variant="light" size="sm" mb="xs">
            {expired
              ? t("parking:status.expired", { defaultValue: "Expired" })
              : hasDuration
                ? t("parking:status.timed", { defaultValue: "Timed Parking" })
                : t("parking:status.active", { defaultValue: "Parked" })}
          </Badge>

          <Text
            size="xl"
            fw={700}
            c={`${statusColor}.${statusShade + 1}`}
            style={{ fontSize: 32, lineHeight: 1.2, fontVariantNumeric: "tabular-nums" }}
          >
            {hasDuration
              ? expired
                ? `+${formatDuration(remainingMinutes != null ? Math.abs(remainingMinutes) : null)} ${t("parking:timer.overtime", { defaultValue: "over" })}`
                : `${formatDuration(remainingMinutes)} ${t("parking:timer.remaining", { defaultValue: "left" })}`
              : formatDuration(elapsedMinutes)}
          </Text>

          <Text size="xs" c="neutral.5" mt={4}>
            {hasDuration
              ? `${t("parking:timer.totalDuration", { defaultValue: "Total" })}: ${formatDuration(session.durationMinutes ?? null)}`
              : t("parking:timer.elapsedLabel", { defaultValue: "Time parked" })}
          </Text>

          {hasDuration && (
            <Progress
              value={progressPercent}
              color={expired ? "error" : progressPercent > 80 ? "warning" : "brand"}
              size="sm"
              mt="sm"
              radius="xl"
              animated={!expired && progressPercent < 100}
            />
          )}
        </Box>

        {/* ── Add Time (timed sessions only) ── */}
        {hasDuration && (
          <>
            {!showAddTime ? (
              <AppButton
                iconName="ClockPlus"
                variant="secondary"
                size="sm"
                fullWidth
                onClick={handleToggleAddTime}
                disabled={isLoading}
              >
                {t("parking:drawers.detail.actions.addTime", {
                  defaultValue: "Add Time",
                })}
              </AppButton>
            ) : (
              <Box
                p="sm"
                style={{
                  backgroundColor: "var(--mantine-color-neutral-0)",
                  borderRadius: "var(--mantine-radius-sm)",
                  border: "1px solid var(--mantine-color-neutral-2)",
                }}
              >
                <Stack gap="xs">
                  <Text size="sm" fw={500}>
                    {t("parking:drawers.detail.addTime.label", {
                      defaultValue: "Add more time",
                    })}
                  </Text>

                  <SegmentedControl
                    value={addTimePreset}
                    onChange={handleAddTimePresetChange}
                    data={addTimeSegmentData}
                    fullWidth
                    size="xs"
                  />

                  {addTimePreset === "custom" && (
                    <NumberInput
                      placeholder={t(
                        "parking:drawers.detail.addTime.customPlaceholder",
                        { defaultValue: "Minutes" }
                      )}
                      value={customAddMinutes ?? ""}
                      onChange={handleCustomAddMinutesChange}
                      min={1}
                      max={1440}
                      size="sm"
                      hideControls
                      rightSection={
                        <Text size="xs" c="dimmed" pr="xs">
                          {t("common:units.min", { defaultValue: "min" })}
                        </Text>
                      }
                      rightSectionWidth={40}
                    />
                  )}

                  <Group gap="xs" justify="flex-end">
                    <AppButton
                      variant="secondary"
                      size="xs"
                      onClick={handleToggleAddTime}
                      disabled={isLoading}
                    >
                      {t("common:actions.cancel", { defaultValue: "Cancel" })}
                    </AppButton>
                    <AppButton
                      variant="primary"
                      size="xs"
                      onClick={handleConfirmAddTime}
                      disabled={!addTimeMinutes || isLoading}
                      loading={updating}
                    >
                      {addTimeMinutes
                        ? t("parking:drawers.detail.addTime.confirm", {
                          defaultValue: `Add ${formatDuration(addTimeMinutes)}`,
                          duration: formatDuration(addTimeMinutes),
                        })
                        : t("parking:drawers.detail.addTime.confirmDefault", {
                          defaultValue: "Add",
                        })}
                    </AppButton>
                  </Group>
                </Stack>
              </Box>
            )}
          </>
        )}

        {/* ── Location ── */}
        {(hasCoordinates || session?.formattedAddress) && (
          <>
            <Divider
              label={t("parking:drawers.detail.sections.location", {
                defaultValue: "Location",
              })}
              labelPosition="center"
            />

            {session?.formattedAddress && (
              <Group gap="xs" wrap="nowrap">
                <AppIcon
                  iconName="MapPin"
                  size={16}
                  color="var(--mantine-color-neutral-5)"
                  style={{ flexShrink: 0 }}
                />
                <Text size="sm">{session.formattedAddress}</Text>
              </Group>
            )}

            {hasCoordinates && (
              <LocationPreview
                latitude={session?.latitude!}
                longitude={session?.longitude!}
                showMap={true}
                mapHeight={160}
                zoom={16}
                showCoordinates={true}
              />
            )}
          </>
        )}

        {/* ── Photo ── */}
        {renderPhotoSection()}

        {/* ── Details ── */}
        <Divider
          label={t("parking:drawers.detail.sections.details", {
            defaultValue: "Details",
          })}
          labelPosition="center"
        />

        {session?.car && (
          <Group gap="xs" wrap="nowrap">
            <AppIcon
              iconName="Car"
              size={16}
              color="var(--mantine-color-neutral-5)"
              style={{ flexShrink: 0 }}
            />
            <Text size="sm">{session.car.label}</Text>
          </Group>
        )}

        {session?.initialPrice != null && session?.initialPrice > 0 && (
          <Group gap="xs" wrap="nowrap">
            <AppIcon
              iconName="CurrencyDollar"
              size={16}
              color="var(--mantine-color-neutral-5)"
              style={{ flexShrink: 0 }}
            />
            <Text size="sm">
              {session?.initialPrice.toFixed(2)} {session?.currency || ""}
            </Text>
          </Group>
        )}

        {session?.userStarted && (
          <Group gap="xs" wrap="nowrap">
            <AppIcon
              iconName="User"
              size={16}
              color="var(--mantine-color-neutral-5)"
              style={{ flexShrink: 0 }}
            />
            <Text size="sm">
              {t("parking:drawers.detail.startedBy", { defaultValue: "Started by" })}{" "}
              {session.userStarted.firstName} {session.userStarted.lastName}
            </Text>
          </Group>
        )}

        {/* ── Travel ── */}
        <Divider
          label={t("parking:drawers.detail.sections.travel", {
            defaultValue: "Travel",
          })}
          labelPosition="center"
        />

        {renderTravelSection(true)}

        {/* ── Notes ── */}
        {session?.notes && (
          <Box
            p="sm"
            style={{
              backgroundColor: "var(--mantine-color-neutral-0)",
              borderRadius: "var(--mantine-radius-sm)",
            }}
          >
            <Group gap="xs" mb={4}>
              <AppIcon
                iconName="Notes"
                size={14}
                color="var(--mantine-color-neutral-5)"
              />
              <Text size="xs" fw={500} c="neutral.5">
                {t("parking:drawers.detail.sections.notes", {
                  defaultValue: "Notes",
                })}
              </Text>
            </Group>
            <Text size="sm">{session.notes}</Text>
          </Box>
        )}
      </Stack>
    );

    // ============================================
    // Render: End Mode content (form only, no buttons)
    // ============================================

    const renderEndMode = () => (
      <Stack gap="md">
        {/* Start Time */}
        <DateTimePicker
          label={t("parking:drawers.end.fields.startTime.label", {
            defaultValue: "Start time",
          })}
          value={endStartTime}
          onChange={handleEndStartTimeChange}
          maxDate={new Date()}
          size="md"
          clearable={false}
          valueFormat="MMM DD, YYYY HH:mm"
          dropdownType={isMobile ? "modal" : "popover"}
          timePickerProps={{
            withDropdown: true,
            popoverProps: { withinPortal: true },
            format: "12h",
          }}
        />

        {/* End Time */}
        <DateTimePicker
          label={t("parking:drawers.end.fields.endTime.label", {
            defaultValue: "End time",
          })}
          value={endTime}
          onChange={handleEndTimeChange}
          maxDate={new Date()}
          size="md"
          clearable={false}
          valueFormat="MMM DD, YYYY HH:mm"
          dropdownType={isMobile ? "modal" : "popover"}
          timePickerProps={{
            withDropdown: true,
            popoverProps: { withinPortal: true },
            format: "12h",
          }}
        />

        {/* Total Duration */}
        {endTotalDuration != null && (
          <Group gap="xs" ml="xs">
            <AppIcon
              iconName="Clock"
              size={14}
              color="var(--mantine-color-brand-6)"
            />
            <Text size="sm" c="brand.7" fw={500}>
              {t("parking:drawers.end.totalDuration", {
                defaultValue: "Total duration",
              })}
              : {formatDuration(endTotalDuration)}
            </Text>
          </Group>
        )}

        {/* ── Travel ── */}
        <Divider
          label={t("parking:drawers.end.sections.travel", {
            defaultValue: "Travel",
          })}
          labelPosition="center"
        />

        {renderTravelSection(false)}

        <Divider
          label={t("parking:drawers.end.sections.price", {
            defaultValue: "Final Price",
          })}
          labelPosition="center"
        />

        {/* Final Price */}
        <Group gap="sm" align="flex-end" wrap="nowrap">
          <NumberInput
            label={t("parking:drawers.end.fields.finalPrice.label", {
              defaultValue: "Final amount",
            })}
            description={t("parking:drawers.end.fields.finalPrice.description", {
              defaultValue:
                "Leave empty or 0 for free parking (no expense will be created)",
            })}
            placeholder="0.00"
            value={finalPrice ?? ""}
            onChange={handleFinalPriceChange}
            min={0}
            max={99999}
            decimalScale={2}
            fixedDecimalScale
            size="md"
            hideControls
            style={{ flex: 1 }}
          />
          <CurrencySelector
            value={endCurrency}
            onChange={handleEndCurrencyChange}
            compact
            compactWidth={120}
            required={true}
            size="md"
            priorityOnly
            withinPortal
          />
        </Group>

        {/* Expense hint */}
        <Group gap="xs" ml="xs">
          <AppIcon
            iconName="InfoCircle"
            size={14}
            color="var(--mantine-color-neutral-4)"
          />
          <Text size="sm" c="neutral.5">
            {finalPrice != null && finalPrice > 0
              ? t("parking:drawers.end.hints.expenseWillBeCreated", {
                defaultValue:
                  "A parking expense will be automatically created.",
              })
              : t("parking:drawers.end.hints.noExpense", {
                defaultValue:
                  "No expense will be created for free parking.",
              })}
          </Text>
        </Group>

        {/* Notes */}
        <Textarea
          label={t("parking:drawers.end.fields.notes.label", {
            defaultValue: "Notes",
          })}
          placeholder={t("parking:drawers.end.fields.notes.placeholder", {
            defaultValue: "Any final notes...",
          })}
          value={endNotes}
          onChange={handleEndNotesChange}
          size="md"
          maxLength={500}
          autosize
          minRows={2}
          maxRows={4}
        />
      </Stack>
    );

    // ============================================
    // Main Render
    // ============================================

    return (
      <>
        <AppDrawer
          opened={opened && !!session}
          onClose={onClose}
          title={drawerTitle}
          position="right"
          size="lg"
          responsive
          mobileHeightPercent={90}
          closable={!isLoading}
          loading={false}
          actionType={drawerActionType}
          leftActions={drawerLeftActions}
          customActions={drawerCustomActions}
        >
          {session && (mode === "detail" ? renderDetailMode() : renderEndMode())}
        </AppDrawer>

        {/* ═══ Full-screen Image Preview ═══ */}
        {photoUrl && (
          <ImageViewer
            opened={isPhotoPreviewOpen}
            onClose={handleClosePhotoPreview}
            src={photoUrl}
            alt={
              photoFileName ||
              t("parkingSessions:form.photo.alt", {
                defaultValue: "Parking photo",
              })
            }
            fileName={photoFileName || undefined}
          />
        )}
      </>
    );
  };

export const ActiveParkingDetailDrawer = React.memo(
  ActiveParkingDetailDrawerComponent
);